import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';

import { Button, Grid, makeStyles } from '@material-ui/core';
import DefaultTable from '../components/DefaultTable';
import PageWrapper from '../components/PageWrapper';
import * as Popover from '../components/PopoverMenu';

import { itineraryListColumns } from '../table-configs/columns-schemas';

import { fetchLine } from 'app/store/slices/line';
import { URLS } from 'app/config/urls';
import lineEndpoint from 'app/api/services/lineEndpoint';
import ListsFilters from '../components/ListsFilters';
import moment from 'moment';
import InfoLineCompany from '../components/InfoLineCompany';
import ConfirmDialog from '../../../components/ConfirmDialog';
import Loading from '../../../layouts/Loading';
import SaveBox from '../components/SaveBox';
import { useTableReducer } from '../hooks/useTableReducer';
import { usePersistentFilters } from '../hooks/usePersistentFilters';

const ItinerariesList = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const classes = useStyles();
    const dispatch = useDispatch();

    const [itineraries, actions] = useTableReducer();
    const { changePage, changePageSize, resetTable, loadData: loadItineraries } = actions;

    const cdLinhaFromPath = React.useRef(match.params.cdLinha);
    const selectedLine = useSelector((state) => state.line.selected);
    const isStrip = useSelector((state) => state.line.isStrip);

    const [loading, setLoading] = useState(false);
    const [loadingRows, setLoadingRows] = useState(true);
    const [editEl, setEditEl] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [orderBy, setOrderBy] = useState('linhaItinerario.dtCadastro');
    const [orderDirection, setOrderDirection] = useState('desc');
    const [canAddNewRevision, setCanAddNewRevision] = useState(false);

    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [cdToInativate, setCdToInativate] = useState(null);


    const initialFilters = useMemo(() => {
        const result = {
            campoOrdenacao: orderBy,
            direcaoOrdenacao: orderDirection,
        };
        if (cdLinhaFromPath && cdLinhaFromPath.current) {
            result.cdLinha = Number(cdLinhaFromPath.current);
        }
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cdLinhaFromPath]);

    const [filters, setFilters] = usePersistentFilters(initialFilters, 'itineraries-filters');


    const [rows, setRows] = useState([]);
    const open = Boolean(editEl);

    useEffect(() => {
        (async () => {
            setLoading(true);
            if (cdLinhaFromPath && cdLinhaFromPath.current) {
                await dispatch(fetchLine(cdLinhaFromPath.current));
            }
            setLoading(false);
        })();
    }, [dispatch]);

    useEffect(() => {
        (async () => {
            setLoadingRows(true);
            const params = {
                page: filters.page ? filters.page : itineraries.page,
                size: filters.pageSize ? filters.pageSize : itineraries.pageSize,
                request: filters
            };
            const response = await lineEndpoint.getItineraries(params);
            if (response && response.status === 200) {
                if (response.data.content.length === 0) {
                    resetTable();
                } else {
                    loadItineraries(response.data);
                }
            }
            setLoadingRows(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itineraries.page, itineraries.pageSize, filters]);

    useEffect(() => {
        (() => {
            if (selectedLine) {
                setCanAddNewRevision(!selectedLine.hasItinerarioVigenteEmRevisao);
            }
        })();
    }, [selectedLine]);

    const prepareRows = (data) => {
        if (!data) return [];
        return data.map((row) => {
            const temp = { ...row };
            temp.dtCadastro = moment(temp.dtCadastro).format('DD/MM/YYYY');
            if (!temp.nuAditivo || temp.nuAditivo === '') temp.nuAditivo = '-';
            temp.deTipoItinerario = temp.tipoItinerario?.deTipoItinerario;
            temp.deLinhaSituacao = temp.situacao?.deLinhaSituacao;
            return temp;
        });
    };

    useEffect(() => {
        (() => {
            setRows(prepareRows(itineraries.content));
        })();
    }, [itineraries]);

    const handleOpenEditMenu = (event, data) => {
        setEditEl(event.currentTarget);
        setSelectedEntry(data);
    };

    const handleCloseEditMenu = () => {
        setSelectedEntry(null);
        setEditEl(null);
    };

    const handleChangePage = (e, page) => {
        changePage(page);
        const temp = { ...filters };
        temp['page'] = page;
        setFilters(temp);
        console.log({ page });
    };

    const handleChangePageSize = (e) => {
        let pageSize = parseInt(e.target.value);
        changePageSize(pageSize);
        const temp = { ...filters };
        temp['pageSize'] = pageSize;
        setFilters(temp);
    };

    const handleChangeOrder = (event, property) => {
        let tempDirection = orderDirection;
        if (property === orderBy) {
            tempDirection = orderDirection === 'desc' ? 'asc' : 'desc';
        }
        const temp = { ...filters };
        temp['campoOrdenacao'] = `linhaItinerario.${property}`;
        if (property === 'deTipoItinerario') {
            temp['campoOrdenacao'] = `tipoItinerario.${property}`;
        } else if (property === 'deLinhaSituacao') {
            temp['campoOrdenacao'] = `situacao.${property}`;
        }
        temp['direcaoOrdenacao'] = tempDirection;
        setOrderDirection(tempDirection);
        setOrderBy(property);
        setFilters(temp);
        handleChangePage(null, 0);
    };

    const handleNewItineraryButton = async () => {
        let params = { cdLinha: match.params.cdLinha };
        if (isStrip) params.isStrip = 'strip';
        const url = generatePath(URLS.LINES_MANAGE_ITINERARIES_NEW, params);
        history.push(url);
    };

    const inativate = async () => {
        setLoading(true);
        setOpenConfirmModal(false);
        const response = await lineEndpoint.inativateItinerary(cdToInativate);
        if (response && response.status === 200 && response.data) {
            window.location.reload();
        }
    };

    const renderConfirmDialog = () => {
        return (
            <ConfirmDialog
                open={openConfirmModal}
                onClose={() => setOpenConfirmModal(false)}
                handleClickNo={() => {
                    handleCloseEditMenu();
                    setOpenConfirmModal(false);
                    setCdToInativate(null);
                }}
                handleClickYes={() => {
                    handleCloseEditMenu();
                    inativate();
                }}
                text="Deseja inativar esta revisão?"
            />
        );
    };

    const handleGoBack = () => {
        let params = { cdLinha: match.params.cdLinha };
        if (isStrip) params.isStrip = 'strip';
        history.push(generatePath(URLS.LINES_MANAGE, params));
    };

    return (
        <>
            {loading && <Loading />}
            {cdToInativate && renderConfirmDialog()}
            <PageWrapper
                title="Lista de itinerário"
                actionButton={
                    canAddNewRevision ? (
                        <Button
                            variant="contained"
                            color="primary"
                            disableElevation
                            className={classes.addButton}
                            onClick={handleNewItineraryButton}>
                            Nova revisão
                        </Button>
                    ) : (
                        <></>
                    )
                }>
                <Grid container spacing={4}>
                    <Grid item container spacing={3} direction={'row'}>
                        <InfoLineCompany selectedLine={selectedLine} />
                    </Grid>

                    <ListsFilters
                        filters={filters}
                        setFilters={setFilters}
                        cdLinha={cdLinhaFromPath.current}
                        type={'itinerary'}
                    />

                    <Grid item xs={12}>
                        <DefaultTable
                            columns={itineraryListColumns}
                            rows={rows}
                            loading={loadingRows}
                            onMore={handleOpenEditMenu}
                            paginationProps={{
                                rowsPerPageOptions: [5, 10, 25],
                                count: itineraries.count,
                                rowsPerPage: filters.pageSize ? filters.pageSize : itineraries.pageSize,
                                page: filters.page ? filters.page : itineraries.page,
                                onPageChange: handleChangePage,
                                onRowsPerPageChange: handleChangePageSize,
                            }}
                            orderBy={orderBy}
                            order={orderDirection}
                            onRequestSort={handleChangeOrder}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SaveBox
                            firstButtonLabel="Voltar"
                            onCancel={handleGoBack}
                            middleButtonLabel={null}
                            lastButtonLabel={null}
                            removeSpacing
                        />
                    </Grid>
                </Grid>
            </PageWrapper>
            <Popover.ItinerariesList
                onEdit={handleOpenEditMenu}
                open={open}
                anchorEl={editEl}
                handleClose={handleCloseEditMenu}
                dataPath={`${URLS.LINES_MANAGE}/${match.params.cdLinha}/itineraries`}
                dataId={selectedEntry?.cdLinhaItinerario}
                data={selectedEntry}
                isActive={selectedEntry?.boAtivo}
                setCdToInativate={setCdToInativate}
                setOpenConfirmModal={setOpenConfirmModal}
                isStrip={isStrip}
            />
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    addButton: {
        borderRadius: '25px',
        color: theme.palette.primary.contrastText,
    },
}));

export default ItinerariesList;
